import { useState, useEffect, useMemo } from "react";
import { useTheme } from "../context/ThemeContext";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Plus,
  MoreHorizontal,
  Pencil,
  Trash2,
  RefreshCw,
  ExternalLink,
  Globe,
  BarChart3,
  FolderOpen,
  Zap,
  Server,
  Star,
  Shield,
  ChevronDown,
  ChevronRight,
  GripVertical,
  ArrowUpDown,
  Clock,
  ArrowDownAZ,
  ArrowUpAZ,
} from "lucide-react";

// Sortable Service Card
const SortableServiceCard = ({ server, onEdit, onDelete, onCheck, onView, onToggleFavorite, onToggleCritical, isDragging }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: server.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <ServiceCard
        server={server}
        onEdit={onEdit}
        onDelete={onDelete}
        onCheck={onCheck}
        onView={onView}
        onToggleFavorite={onToggleFavorite}
        onToggleCritical={onToggleCritical}
        onToggleCritical={onToggleCritical}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
};

// Service Card
const ServiceCard = ({ server, onEdit, onDelete, onCheck, onView, onToggleFavorite, onToggleCritical, dragHandleProps }) => {
  const primaryAddress = server.addresses?.[0]?.url || "";
  
  const handleOpen = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (primaryAddress) {
      const fullUrl = primaryAddress.startsWith('http') ? primaryAddress : `https://${primaryAddress}`;
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const getStatusClass = () => {
    if (server.status === "online") return "status-indicator status-online";
    if (server.status === "offline") return "status-indicator status-offline";
    return "status-indicator status-unknown";
  };

  const formatResponseTime = (ms) => {
    if (ms === null || ms === undefined) return null;
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  return (
    <div
      className="service-card group relative"
      onClick={handleOpen}
      data-testid={`server-card-${server.id}`}
    >
      {/* Favorite Star - Top Left */}
      {server.is_favorite && (
        <div className="absolute -top-1.5 -left-1.5 z-10">
          <div className="w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center shadow-sm">
            <Star className="w-3 h-3 text-white fill-current" />
          </div>
        </div>
      )}

      {/* Critical Shield - Top Right */}
      {server.is_critical && (
        <div className="absolute -top-1.5 -right-1.5 z-10">
          <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center shadow-sm">
            <Shield className="w-3 h-3 text-white fill-current" />
          </div>
        </div>
      )}

      {/* Drag Handle */}
      {dragHandleProps && (
        <div
          className="absolute left-1 top-1/2 -translate-y-1/2 p-1 cursor-grab opacity-0 group-hover:opacity-50 hover:!opacity-100 transition-opacity"
          {...dragHandleProps}
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="w-4 h-4 text-muted-foreground" />
        </div>
      )}

      {/* Icon */}
      <div className={`service-icon ${dragHandleProps ? 'ml-4' : ''}`}>
        {server.favicon_url ? (
          <img
            src={server.favicon_url}
            alt=""
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        ) : (
          <Globe className="w-5 h-5 text-muted-foreground" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium truncate">{server.name}</span>
          <div className={getStatusClass()} />
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
          {primaryAddress && (
            <span className="truncate font-mono">{primaryAddress.replace(/^https?:\/\//, '')}</span>
          )}
          {formatResponseTime(server.response_time) && (
            <span className="quick-pill shrink-0">
              <Zap className="w-3 h-3" />
              {formatResponseTime(server.response_time)}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            data-testid={`server-actions-btn-${server.id}`}
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 opacity-0 group-hover:opacity-100 hover:opacity-100 focus:opacity-100"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreHorizontal className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem 
            onClick={(e) => { e.stopPropagation(); onToggleFavorite(server.id); }}
            data-testid={`favorite-menu-${server.id}`}
          >
            <Star className={`mr-2 h-4 w-4 ${server.is_favorite ? 'fill-amber-500 text-amber-500' : ''}`} />
            {server.is_favorite ? 'Remove from Favorites' : 'Add to Favorites'}
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={(e) => { e.stopPropagation(); onToggleCritical(server.id); }}
            data-testid={`critical-menu-${server.id}`}
          >
            <Shield className={`mr-2 h-4 w-4 ${server.is_critical ? 'fill-red-500 text-red-500' : ''}`} />
            {server.is_critical ? 'Remove Critical Flag' : 'Mark as Critical'}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem 
            onClick={(e) => { e.stopPropagation(); onView(server.id); }}
            data-testid={`view-stats-${server.id}`}
          >
            <BarChart3 className="mr-2 h-4 w-4" />
            View Stats
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={(e) => { e.stopPropagation(); onEdit(server); }}
            data-testid={`edit-menu-${server.id}`}
          >
            <Pencil className="mr-2 h-4 w-4" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={(e) => { e.stopPropagation(); onCheck(server.id); }}
            data-testid={`check-menu-${server.id}`}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Check Now
          </DropdownMenuItem>
          {primaryAddress && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleOpen} data-testid={`open-menu-${server.id}`}>
                <ExternalLink className="mr-2 h-4 w-4" />
                Open
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(server.id);
            }}
            data-testid={`delete-menu-${server.id}`}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

// Sortable Group Header for drag and drop
const SortableGroupHeader = ({ group, isCollapsed, onToggleCollapse, onDeleteGroup, onEditGroup, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: group.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Collapsible open={!isCollapsed} onOpenChange={() => onToggleCollapse(group.id)} className="mb-6">
        <div className="group-header w-full group/header flex items-center">
          {/* Drag Handle */}
          <div
            className="p-1 cursor-grab opacity-0 group-hover/header:opacity-50 hover:!opacity-100 mr-1 transition-opacity"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="w-3 h-3 text-muted-foreground" />
          </div>

          <CollapsibleTrigger asChild>
            <div className="flex items-center gap-2 cursor-pointer hover:text-foreground transition-colors flex-1">
              <div 
                className="group-header-icon"
                style={{ backgroundColor: `${group.color}20`, color: group.color }}
              >
                <FolderOpen className="w-3 h-3" />
              </div>
              <span>{group.name}</span>
              {isCollapsed ? (
                <ChevronRight className="w-4 h-4 ml-auto opacity-50 group-hover/header:opacity-100 transition-opacity" />
              ) : (
                <ChevronDown className="w-4 h-4 ml-auto opacity-50 group-hover/header:opacity-100 transition-opacity" />
              )}
            </div>
          </CollapsibleTrigger>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-5 w-5 ml-1 opacity-0 group-hover/header:opacity-100"
              >
                <MoreHorizontal className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-32">
              <DropdownMenuItem
                onClick={() => onEditGroup(group)}
              >
                <Pencil className="mr-2 h-3 w-3" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => onDeleteGroup(group.id)}
              >
                <Trash2 className="mr-2 h-3 w-3" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <CollapsibleContent>
          {children}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

// Ungrouped Section (not sortable)
const UngroupedSection = ({ servers, isCollapsed, onToggleCollapse, onEdit, onDelete, onCheck, onView, onToggleFavorite, onToggleCritical }) => {
  if (servers.length === 0) return null;

  return (
    <Collapsible open={!isCollapsed} onOpenChange={() => onToggleCollapse('ungrouped')} className="mb-6">
      <div className="group-header w-full group/header flex items-center">
        <CollapsibleTrigger asChild>
          <div className="flex items-center gap-2 cursor-pointer hover:text-foreground transition-colors flex-1">
            <div className="group-header-icon">
              <FolderOpen className="w-3 h-3" />
            </div>
            <span>Ungrouped</span>
            <span className="text-xs font-normal opacity-60">({servers.length})</span>
            {isCollapsed ? (
              <ChevronRight className="w-4 h-4 ml-auto opacity-50 group-hover/header:opacity-100 transition-opacity" />
            ) : (
              <ChevronDown className="w-4 h-4 ml-auto opacity-50 group-hover/header:opacity-100 transition-opacity" />
            )}
          </div>
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mt-3">
          {servers.map((server) => (
            <ServiceCard
              key={server.id}
              server={server}
              onEdit={onEdit}
              onDelete={onDelete}
              onCheck={onCheck}
              onView={onView}
              onToggleFavorite={onToggleFavorite}
              onToggleCritical={onToggleCritical}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// Main ServerGrid component
const ServerGrid = ({
  servers,
  groups,
  onAddServer,
  onAddGroup,
  onEditServer,
  onDeleteServer,
  onCheckServer,
  onViewServer,
  onDeleteGroup,
  onEditGroup,
  onToggleFavorite,
  onToggleCritical,
  onReorderServers,
  onReorderGroups,
}) => {
  const { visualTheme } = useTheme();
  
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const saved = localStorage.getItem('netset-collapsed-groups');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const [sortOption, setSortOption] = useState(() => {
    return localStorage.getItem('netset-sort-option') || 'custom';
  });

  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Save collapsed state
  useEffect(() => {
    localStorage.setItem('netset-collapsed-groups', JSON.stringify(collapsedGroups));
  }, [collapsedGroups]);

  // Save sort option
  useEffect(() => {
    localStorage.setItem('netset-sort-option', sortOption);
  }, [sortOption]);

  const toggleGroupCollapse = (groupId) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // Sort function
  const sortServers = (serverList) => {
    if (sortOption === 'custom') {
      return [...serverList].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    }
    if (sortOption === 'name-asc') {
      return [...serverList].sort((a, b) => a.name.localeCompare(b.name));
    }
    if (sortOption === 'name-desc') {
      return [...serverList].sort((a, b) => b.name.localeCompare(a.name));
    }
    if (sortOption === 'newest') {
      return [...serverList].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
    if (sortOption === 'oldest') {
      return [...serverList].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    }
    return serverList;
  };

  // Sort groups
  const sortedGroups = useMemo(() => {
    if (sortOption === 'custom') {
      return [...groups].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    }
    if (sortOption === 'name-asc') {
      return [...groups].sort((a, b) => a.name.localeCompare(b.name));
    }
    if (sortOption === 'name-desc') {
      return [...groups].sort((a, b) => b.name.localeCompare(a.name));
    }
    return [...groups].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }, [groups, sortOption]);

  // Separate and sort favorites
  const favoriteServers = useMemo(() => {
    return sortServers(servers.filter(s => s.is_favorite));
  }, [servers, sortOption]);

  // Group non-favorite servers
  const groupedServers = useMemo(() => {
    const nonFavorites = servers.filter(s => !s.is_favorite);
    return sortedGroups.reduce((acc, group) => {
      acc[group.id] = sortServers(nonFavorites.filter(s => s.group_id === group.id));
      return acc;
    }, {});
  }, [servers, sortedGroups, sortOption]);

  const ungroupedServers = useMemo(() => {
    return sortServers(servers.filter(s => !s.is_favorite && !s.group_id));
  }, [servers, sortOption]);

  // Handle drag end for favorites
  const handleFavoritesDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    
    if (active.id !== over?.id && onReorderServers) {
      const oldIndex = favoriteServers.findIndex(s => s.id === active.id);
      const newIndex = favoriteServers.findIndex(s => s.id === over.id);
      const newOrder = arrayMove(favoriteServers, oldIndex, newIndex);
      onReorderServers(newOrder.map(s => s.id));
    }
  };

  // Handle drag end for groups
  const handleGroupsDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    
    if (active.id !== over?.id && onReorderGroups) {
      const oldIndex = sortedGroups.findIndex(g => g.id === active.id);
      const newIndex = sortedGroups.findIndex(g => g.id === over.id);
      const newOrder = arrayMove(sortedGroups, oldIndex, newIndex);
      onReorderGroups(newOrder.map(g => g.id));
    }
  };

  // Handle drag end for servers within a group
  const handleGroupServersDragEnd = (groupId) => (event) => {
    const { active, over } = event;
    setActiveId(null);
    
    if (active.id !== over?.id && onReorderServers) {
      const groupServerList = groupedServers[groupId] || [];
      const oldIndex = groupServerList.findIndex(s => s.id === active.id);
      const newIndex = groupServerList.findIndex(s => s.id === over.id);
      const newOrder = arrayMove(groupServerList, oldIndex, newIndex);
      onReorderServers(newOrder.map(s => s.id));
    }
  };

  // Separate expanded and collapsed groups
  const { expandedGroups, collapsedGroupsList } = useMemo(() => {
    const expanded = [];
    const collapsed = [];
    
    sortedGroups.forEach(group => {
      const groupServers = groupedServers[group.id] || [];
      if (groupServers.length === 0) return;
      
      if (collapsedGroups[group.id]) {
        collapsed.push({ ...group, serverCount: groupServers.length });
      } else {
        expanded.push(group);
      }
    });
    
    return { expandedGroups: expanded, collapsedGroupsList: collapsed };
  }, [sortedGroups, groupedServers, collapsedGroups]);

  const hasServers = servers.length > 0;
  const isDragEnabled = sortOption === 'custom';

  return (
    <div>
      {/* Sort Options */}
      {hasServers && (
        <div className="flex justify-end mb-4 relative z-10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground" data-testid="sort-btn">
                <ArrowUpDown className="w-4 h-4" />
                Sort
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 z-50">
              <DropdownMenuLabel>Sort Order</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuRadioGroup value={sortOption} onValueChange={setSortOption}>
                <DropdownMenuRadioItem value="custom">
                  <GripVertical className="w-4 h-4 mr-2" />
                  Custom (Drag & Drop)
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="name-asc">
                  <ArrowDownAZ className="w-4 h-4 mr-2" />
                  Name (A-Z)
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="name-desc">
                  <ArrowUpAZ className="w-4 h-4 mr-2" />
                  Name (Z-A)
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="newest">
                  <Clock className="w-4 h-4 mr-2" />
                  Newest First
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="oldest">
                  <Clock className="w-4 h-4 mr-2" />
                  Oldest First
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {!hasServers ? (
        <div className="empty-state">
          <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mb-4">
            <Server className="w-8 h-8 text-muted-foreground/50" />
          </div>
          <h2 className="text-xl font-semibold mb-2">No servers yet</h2>
          <p className="text-muted-foreground text-sm mb-6 max-w-xs">
            Add your servers, websites, and services for quick access.
          </p>
          <div className="flex gap-2">
            <Button onClick={onAddGroup} variant="outline" size="sm">
              <Plus className="w-4 h-4 mr-1" />
              New Group
            </Button>
            <Button onClick={onAddServer} size="sm">
              <Plus className="w-4 h-4 mr-1" />
              Add Server
            </Button>
          </div>
        </div>
      ) : (
        <>
          {/* Favorites Section with Drag & Drop */}
          {favoriteServers.length > 0 && (
            <div className="mb-10 relative">
              {/* Decorative line - Cyber theme only */}
              {visualTheme === 'cyber' && (
                <div className="absolute left-0 right-0 top-3 h-px bg-gradient-to-r from-amber-500/50 via-amber-500/20 to-transparent" />
              )}
              {/* Matrix scan line */}
              {visualTheme === 'matrix' && (
                <div className="absolute left-0 right-0 top-3 h-px bg-gradient-to-r from-transparent via-green-500/60 to-transparent" style={{ animation: 'matrix-glow-pulse 2s ease-in-out infinite' }} />
              )}
              
              <div className="group-header mb-4 relative">
                <div className={`w-6 h-6 rounded flex items-center justify-center ${
                  visualTheme === 'cyber'
                    ? 'bg-amber-500/20 border border-amber-500/30'
                    : visualTheme === 'steampunk'
                    ? 'bg-amber-700/30 border border-amber-600/40'
                    : visualTheme === 'matrix'
                    ? 'bg-green-500/10 border border-green-500/40'
                    : 'bg-primary/10 border border-primary/20'
                }` + (visualTheme === 'matrix' ? ' rounded-none' : '')}>
                  <Star className={`w-3 h-3 fill-current ${
                    visualTheme === 'cyber'
                      ? 'text-amber-400 drop-shadow-[0_0_5px_rgba(251,191,36,0.5)]'
                      : visualTheme === 'steampunk'
                      ? 'text-amber-500'
                      : visualTheme === 'matrix'
                      ? 'text-green-500 drop-shadow-[0_0_8px_rgba(0,255,0,0.8)]'
                      : 'text-primary'
                  }`} />
                </div>
                <span className={
                  visualTheme === 'cyber' 
                    ? 'text-amber-400' 
                    : visualTheme === 'steampunk'
                    ? 'text-amber-500'
                    : visualTheme === 'matrix'
                    ? 'text-green-500 font-mono'
                    : 'text-primary'
                } style={
                  visualTheme === 'cyber' 
                    ? { textShadow: '0 0 10px rgba(251, 191, 36, 0.5)' } 
                    : visualTheme === 'steampunk'
                    ? { textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)' }
                    : visualTheme === 'matrix'
                    ? { textShadow: '0 0 10px rgba(0, 255, 0, 0.8), 0 0 20px rgba(0, 255, 0, 0.4)' }
                    : {}
                }>
                  {visualTheme === 'cyber' ? 'PRIORITY SYSTEMS' : visualTheme === 'steampunk' ? '✦ PRIZED ENGINES ✦' : visualTheme === 'matrix' ? '> PRIORITY_NODES' : 'Favorites'}
                </span>
                <span className={`text-xs font-normal ${
                  visualTheme === 'cyber' ? 'text-amber-400/50' : visualTheme === 'steampunk' ? 'text-amber-600/60' : visualTheme === 'matrix' ? 'text-green-500/50 font-mono' : 'text-primary/50'
                }`}>({favoriteServers.length})</span>
              </div>

              {isDragEnabled ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={({ active }) => setActiveId(active.id)}
                  onDragEnd={handleFavoritesDragEnd}
                >
                  <SortableContext items={favoriteServers.map(s => s.id)} strategy={rectSortingStrategy}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                      {favoriteServers.map((server) => (
                        <SortableServiceCard
                          key={server.id}
                          server={server}
                          onEdit={onEditServer}
                          onDelete={onDeleteServer}
                          onCheck={onCheckServer}
                          onView={onViewServer}
                          onToggleFavorite={onToggleFavorite}
        onToggleCritical={onToggleCritical}
                          isDragging={activeId === server.id}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {favoriteServers.map((server) => (
                    <ServiceCard
                      key={server.id}
                      server={server}
                      onEdit={onEditServer}
                      onDelete={onDeleteServer}
                      onCheck={onCheckServer}
                      onView={onViewServer}
                      onToggleFavorite={onToggleFavorite}
                      onToggleCritical={onToggleCritical}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Expanded Groups with Drag & Drop */}
          {isDragEnabled ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleGroupsDragEnd}
            >
              <SortableContext items={sortedGroups.map(g => g.id)} strategy={rectSortingStrategy}>
                {expandedGroups.map((group) => {
                  const groupServers = groupedServers[group.id] || [];
                  
                  return (
                    <SortableGroupHeader
                      key={group.id}
                      group={group}
                      isCollapsed={false}
                      onToggleCollapse={toggleGroupCollapse}
                      onDeleteGroup={onDeleteGroup}
                      onEditGroup={onEditGroup}
                    >
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleGroupServersDragEnd(group.id)}
                      >
                        <SortableContext items={groupServers.map(s => s.id)} strategy={rectSortingStrategy}>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mt-3">
                            {groupServers.map((server) => (
                              <SortableServiceCard
                                key={server.id}
                                server={server}
                                onEdit={onEditServer}
                                onDelete={onDeleteServer}
                                onCheck={onCheckServer}
                                onView={onViewServer}
                                onToggleFavorite={onToggleFavorite}
                                onToggleCritical={onToggleCritical}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                    </SortableGroupHeader>
                  );
                })}
              </SortableContext>
            </DndContext>
          ) : (
            expandedGroups.map((group) => {
              const groupServers = groupedServers[group.id] || [];
              
              return (
                <div key={group.id} className="mb-6">
                  <div 
                    className="group-header w-full group/header flex items-center cursor-pointer"
                    onClick={() => toggleGroupCollapse(group.id)}
                  >
                    <div className="flex items-center gap-2 hover:text-foreground transition-colors flex-1">
                      <div 
                        className="group-header-icon"
                        style={{ backgroundColor: `${group.color}20`, color: group.color }}
                      >
                        <FolderOpen className="w-3 h-3" />
                      </div>
                      <span>{group.name}</span>
                      <span className="text-xs font-normal opacity-60">({groupServers.length})</span>
                      <ChevronDown className="w-4 h-4 ml-auto opacity-50" />
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-5 w-5 ml-1 opacity-0 group-hover/header:opacity-100">
                          <MoreHorizontal className="w-3 h-3" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-32">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEditGroup(group); }}>
                          <Pencil className="mr-2 h-3 w-3" />
                          Rename
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={(e) => { e.stopPropagation(); onDeleteGroup(group.id); }}
                        >
                          <Trash2 className="mr-2 h-3 w-3" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mt-3">
                    {groupServers.map((server) => (
                      <ServiceCard
                        key={server.id}
                        server={server}
                        onEdit={onEditServer}
                        onDelete={onDeleteServer}
                        onCheck={onCheckServer}
                        onView={onViewServer}
                        onToggleFavorite={onToggleFavorite}
                        onToggleCritical={onToggleCritical}
                      />
                    ))}
                  </div>
                </div>
              );
            })
          )}

          {/* Collapsed Groups as Compact Buttons */}
          {collapsedGroupsList.length > 0 && (
            <div className="mt-6 pt-6 border-t border-border/50">
              <div className="flex items-center gap-2 mb-4 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                <FolderOpen className="w-3 h-3" />
                <span>More Groups</span>
                <span className="opacity-60">({collapsedGroupsList.length})</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                {collapsedGroupsList.map((group) => (
                  <button
                    key={group.id}
                    onClick={() => toggleGroupCollapse(group.id)}
                    className="group flex items-center gap-2 px-3 py-2.5 rounded-lg bg-card border border-border/50 hover:border-border hover:bg-accent/50 transition-all text-left"
                  >
                    <div 
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: group.color }}
                    />
                    <span className="text-sm font-medium truncate flex-1">{group.name}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{group.serverCount}</span>
                    <ChevronRight className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Ungrouped Section */}
          <UngroupedSection
            servers={ungroupedServers}
            isCollapsed={collapsedGroups['ungrouped']}
            onToggleCollapse={toggleGroupCollapse}
            onEdit={onEditServer}
            onDelete={onDeleteServer}
            onCheck={onCheckServer}
            onView={onViewServer}
            onToggleFavorite={onToggleFavorite}
            onToggleCritical={onToggleCritical}
          />

          {/* Quick Add */}
          <div className="flex gap-2 mt-4">
            <Button onClick={onAddServer} variant="ghost" size="sm" className="text-muted-foreground">
              <Plus className="w-4 h-4 mr-1" />
              Add Server
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default ServerGrid;
